import { useCallback, useMemo, useState } from "react";
import { InitialProctoringState, UnsavedAssessmentInfo } from "../editQuizModalWrapper";
import { VerityApi } from "../../api/fetch";
import {
	convertProctoringAssessmentConfiguration,
	convertProctoringOption,
	getDefaultProctoringOption,
	isValidPlatform,
} from "../editQuizHelper";
import { ProctoringAssessmentConfigurationV2, ProctoringOption } from "@verity/proctoring-option/types";
import { coreToast } from "../../core";
import type { CoreFileItem } from "../../core";
import { VerityLogger } from "@yujacorp/verity-logger";
import { DEFAULT_ENABLED_ASSESSMENT_CONFIGURATION } from "@verity/proctoring-option";
import {
	convertLiveSessionsToPendingSessions,
	type LiveSessionWithInvitations,
	type PendingLiveSession,
} from "../live/liveHelper";
import password from "../password";
import type { EditQuizType } from "../../types/common";

export type UseEditQuizWrapperProps = {
	api: VerityApi;
	quizInfo: UnsavedAssessmentInfo;
	quizType?: EditQuizType;
	forceThirdParty?: boolean;
	initialPlatformId?: number;
	/**
	 * Settings to open with for a caller that has a previous save but no assessment to fetch it from — the
	 * Lumina configurator, whose last save comes back in the postMessage that launches it. Read only on the
	 * no-quiz-id branch below; with a quiz id `getProctoringAssessmentConfiguration` is the only source.
	 *
	 * Its `enabled` is load-bearing beyond the flag itself: `shouldStartOnLastStep` (`useNavState.ts`) reads
	 * it off `initialProctoringAssessmentConfiguration` to open an already-configured assessment on the
	 * review step instead of at step one, which is exactly what a caller passing settings here wants.
	 */
	initialAssessmentConfiguration?: Omit<Partial<ProctoringAssessmentConfigurationV2>, "proctoringOptionProfile">;
	onInitialOptions?: (
		proctoringAssessmentConfiguration: Omit<Partial<ProctoringAssessmentConfigurationV2>, "proctoringOptionProfile">,
		proctoringOption: Partial<ProctoringOption>,
	) => void;
	logger?: VerityLogger;
	onEnabledChange: (
		proctoringAssessmentConfiguration: Pick<Partial<ProctoringAssessmentConfigurationV2>, "enabled" | "password">,
		onPasswordGenerated: () => void,
	) => void | Promise<void>;
};

export default function useEditQuizWrapper({
	api,
	quizInfo,
	quizType,
	forceThirdParty,
	initialPlatformId,
	initialAssessmentConfiguration,
	onInitialOptions,
	logger,
	onEnabledChange,
}: UseEditQuizWrapperProps) {
	const {
		getProctoringAssessmentConfiguration,
		areProctoringProfilesEnforced,
		getDefaultProctoringProfile,
		getVerityInstituteSettings,
		validateAssessment,
	} = api;

	const [enforcedProfilesOnly, setEnforcedProfilesOnly] = useState<boolean>(true);
	const [defaultProctoringOption, setDefaultProctoringOption] = useState<ProctoringOption | null>(null);
	const [initialEnabledValue, setInitialEnabledValue] = useState<boolean | undefined>(undefined);
	const [initialProctoringAssessmentConfiguration, setInitialProctoringAssessmentConfiguration] =
		useState<Omit<Partial<ProctoringAssessmentConfigurationV2>, "proctoringOptionProfile">>();
	const [proctoringAssessmentConfiguration, setProctoringAssessmentConfiguration] =
		useState<Omit<Partial<ProctoringAssessmentConfigurationV2>, "proctoringOptionProfile">>();
	const [initialProctoringOption, setInitialProctoringOption] = useState<InitialProctoringState>();
	const [proctoringOption, setProctoringOption] = useState<Partial<ProctoringOption>>();
	const [currentProfileId, setCurrentProfileId] = useState<number>();
	const [initialProfileId, setInitialProfileId] = useState<number>();
	const [pendingLiveSessions, setPendingLiveSessions] = useState<Record<string, PendingLiveSession>>({});
	const [pendingLiveSessionIds, setPendingLiveSessionIds] = useState<string[]>([]);
	const [existingLiveSessions, setExistingLiveSessions] = useState<Record<string, LiveSessionWithInvitations>>({});
	const [existingReferenceMaterialFiles, setExistingReferenceMaterialFiles] = useState<CoreFileItem[] | undefined>(
		undefined,
	);

	const refreshSettings = useCallback(
		(confirmedOption?: InitialProctoringState) => {
			void (async () => {
				setEnforcedProfilesOnly(await areProctoringProfilesEnforced());
				const savedDefaultProfile = await getDefaultProctoringProfile();
				const verityInstituteSettings = await getVerityInstituteSettings();
				const defaultProctoringOption = getDefaultProctoringOption(verityInstituteSettings, quizType);
				setDefaultProctoringOption(defaultProctoringOption);

				const defaultProfile =
					savedDefaultProfile &&
					isValidPlatform({ option: savedDefaultProfile.proctoringOption, verityInstituteSettings, quizType })
						? savedDefaultProfile
						: undefined;
				const defaultInitialProctoringOption = defaultProfile?.proctoringOption ?? defaultProctoringOption;

				const quizId = quizInfo?.quizId;
				let getProctoringAssessmentConfigurationResp: ProctoringAssessmentConfigurationV2 | null = null;
				if (quizId) {
					const quizInfoWithId = {
						...quizInfo,
						quizId,
					};
					getProctoringAssessmentConfigurationResp = await getProctoringAssessmentConfiguration(quizInfoWithId);
				}

				if (getProctoringAssessmentConfigurationResp && quizId) {
					const { proctoringOptionProfile, ...configuration } = getProctoringAssessmentConfigurationResp;
					const convertedConfiguration = convertProctoringAssessmentConfiguration(
						configuration,
						forceThirdParty,
						initialPlatformId,
					);
					if (initialEnabledValue === undefined) {
						setInitialEnabledValue(convertedConfiguration.enabled);
					}
					setInitialProctoringAssessmentConfiguration({ ...convertedConfiguration });
					setProctoringAssessmentConfiguration(convertedConfiguration);
					setInitialProctoringOption(convertProctoringOption(proctoringOptionProfile.proctoringOption));
					setProctoringOption(convertProctoringOption(proctoringOptionProfile.proctoringOption));
					setCurrentProfileId(proctoringOptionProfile.id);
					setInitialProfileId(proctoringOptionProfile.id);
					onInitialOptions?.(configuration, proctoringOptionProfile.proctoringOption);
					setExistingReferenceMaterialFiles(confirmedOption?.existingReferenceMaterialFiles ?? undefined);

					/**
					 * If we have initial options, and Verity is enabled, validate the configuration
					 */
					if (convertedConfiguration.enabled) {
						try {
							const { didValidate, ...validations } = await validateAssessment({
								courseId: quizInfo.courseId,
								quizId,
							});

							if (!didValidate) return;

							if (validations.isPasswordRequired && !validations.doPasswordsMatch) {
								coreToast.error(
									"The proctoring settings could not be validated. Please resave the settings for the assessment.",
									{
										containerId: `toast-${quizInfo.quizId}`,
									},
								);
							}
						} catch (e) {
							void logger?.error(e);
						}
					}
				} else {
					const convertedConfiguration = convertProctoringAssessmentConfiguration(
						{
							...DEFAULT_ENABLED_ASSESSMENT_CONFIGURATION,
							enabled: Boolean(proctoringAssessmentConfiguration && proctoringAssessmentConfiguration?.enabled),
							...initialAssessmentConfiguration,
						},
						forceThirdParty,
						initialPlatformId,
					);
					if (initialEnabledValue === undefined || !quizInfo.quizId) {
						setInitialEnabledValue(convertedConfiguration.enabled);
					}
					if (convertedConfiguration.enabled && proctoringAssessmentConfiguration?.password) {
						convertedConfiguration.password = proctoringAssessmentConfiguration.password;
					}
					setInitialProctoringAssessmentConfiguration(
						proctoringAssessmentConfiguration ?? { ...convertedConfiguration },
					);
					setProctoringAssessmentConfiguration(proctoringAssessmentConfiguration ?? convertedConfiguration);
					setInitialProctoringOption(confirmedOption ?? initialProctoringOption ?? defaultInitialProctoringOption);
					setProctoringOption(confirmedOption ?? initialProctoringOption ?? defaultInitialProctoringOption);
					const profileId = confirmedOption?.profileId ?? initialProctoringOption?.profileId ?? defaultProfile?.id;
					setCurrentProfileId(profileId);
					setInitialProfileId(profileId);
					setExistingReferenceMaterialFiles(confirmedOption?.existingReferenceMaterialFiles ?? undefined);

					const pendingSrc = confirmedOption?.pendingLiveSessions ?? initialProctoringOption?.pendingLiveSessions;
					const hasPendingLiveSessions = pendingSrc && Object.keys(pendingSrc).length > 0;

					if (hasPendingLiveSessions) {
						setPendingLiveSessions(pendingSrc);
						const sortedIds = Object.values(pendingSrc).map((session) => session.identifier);
						setPendingLiveSessionIds(sortedIds);
					}

					onInitialOptions?.({}, confirmedOption ?? initialProctoringOption ?? defaultInitialProctoringOption);
				}
			})();
		},
		[
			areProctoringProfilesEnforced,
			forceThirdParty,
			getDefaultProctoringProfile,
			getProctoringAssessmentConfiguration,
			getVerityInstituteSettings,
			initialAssessmentConfiguration,
			initialEnabledValue,
			initialPlatformId,
			initialProctoringOption,
			logger,
			onInitialOptions,
			proctoringAssessmentConfiguration,
			quizInfo,
			quizType,
			validateAssessment,
		],
	);

	const handleEnabledChange = useCallback(
		async (enabled: boolean) => {
			if (!proctoringAssessmentConfiguration || !quizInfo.courseId) return false;
			proctoringAssessmentConfiguration.enabled = enabled;
			proctoringAssessmentConfiguration.password = proctoringAssessmentConfiguration.password
				? proctoringAssessmentConfiguration.password
				: quizInfo.quizId
					? password.generateKey()
					: null;
			try {
				if (proctoringAssessmentConfiguration) {
					setProctoringAssessmentConfiguration({ ...proctoringAssessmentConfiguration });
				}
				await onEnabledChange(proctoringAssessmentConfiguration, () => undefined);
			} catch (err) {
				console.error(err);
				alert("Something went wrong.");
			}
			return true;
		},
		[onEnabledChange, proctoringAssessmentConfiguration, quizInfo.courseId, quizInfo.quizId],
	);

	/**
	 * Restores everything the edit quiz modal edits back to the values it was opened with, so closing it
	 * without saving discards the changes (proctoring method, live sessions, options, profile and
	 * reference materials). Values the modal only mirrors are kept as they are: the enable switch and the
	 * assessment's third party URL both live on the LMS page and are not the modal's to discard.
	 */
	const discardSettings = useCallback(
		(previousReferenceMaterialFiles?: CoreFileItem[]) => {
			setProctoringAssessmentConfiguration((currentConfiguration) => {
				if (!initialProctoringAssessmentConfiguration) return currentConfiguration;
				const { thirdPartyAssessmentOption } = initialProctoringAssessmentConfiguration;

				return {
					...initialProctoringAssessmentConfiguration,
					enabled: currentConfiguration?.enabled,
					password: currentConfiguration?.password,
					...(thirdPartyAssessmentOption && {
						thirdPartyAssessmentOption: {
							...thirdPartyAssessmentOption,
							thirdPartyUrl: currentConfiguration?.thirdPartyAssessmentOption?.thirdPartyUrl,
						},
					}),
				};
			});
			setProctoringOption(initialProctoringOption);
			setCurrentProfileId(initialProfileId);
			setExistingReferenceMaterialFiles(previousReferenceMaterialFiles);

			// Assessments that do not exist in the LMS yet keep their sessions in initialProctoringOption,
			// every other assessment falls back to the sessions last fetched from the server
			const initialPendingLiveSessions =
				initialProctoringOption?.pendingLiveSessions ??
				convertLiveSessionsToPendingSessions(Object.values(existingLiveSessions));
			setPendingLiveSessions(initialPendingLiveSessions);
			setPendingLiveSessionIds(Object.values(initialPendingLiveSessions).map(({ identifier }) => identifier));
		},
		[existingLiveSessions, initialProctoringAssessmentConfiguration, initialProctoringOption, initialProfileId],
	);

	const pendingLiveSessionsArray = useMemo(() => {
		return pendingLiveSessionIds.map((id) => pendingLiveSessions[id]);
	}, [pendingLiveSessionIds, pendingLiveSessions]);

	return {
		state: {
			enforcedProfilesOnly,
			defaultProctoringOption,
			initialEnabledValue,
			initialProctoringAssessmentConfiguration,
			proctoringAssessmentConfiguration,
			initialProctoringOption,
			proctoringOption,
			currentProfileId,
			pendingLiveSessions,
			pendingLiveSessionIds,
			pendingLiveSessionsArray,
			existingLiveSessions,
			existingReferenceMaterialFiles,
		},
		action: {
			refreshSettings,
			setProctoringAssessmentConfiguration,
			setProctoringOption,
			setPendingLiveSessions,
			setPendingLiveSessionIds,
			setCurrentProfileId,
			setExistingLiveSessions,
			handleEnabledChange,
			setInitialEnabledValue,
			discardSettings,
		},
	};
}

export type UseEditQuizWrapperReturn = ReturnType<typeof useEditQuizWrapper>;
